import { Component, OnInit, OnDestroy, ViewChild, ElementRef, AfterViewChecked } from '@angular/core';
import { AiChatService, ChatMessage } from '../../../services/ai-chat.service';
import { AppContextService, AppContext } from '../../../services/app-context.service';
import { UserService } from '../../../services/user.service';
import { HttpTokenService } from '../../../services/http.token.service';
import { Subscription } from 'rxjs';
import { AlertService } from "../../../_alert";

/**
 * AI Chat Window Component
 * Main chat interface with messages, input, and suggested prompts
 */
@Component({
    selector: 'app-ai-chat-window',
    templateUrl: './ai-chat-window.component.html',
    styleUrls: ['./ai-chat-window.component.scss']
})
export class AiChatWindowComponent implements OnInit, OnDestroy, AfterViewChecked {
    @ViewChild('messagesContainer') private messagesContainer!: ElementRef;
    @ViewChild('messageInput') private messageInput!: ElementRef;

    public isOpen = false;
    public isCollapsed: boolean;
    public isMinimized = false;
    public isLeftPanelOpen = true; // Control left panel visibility
    public messages: ChatMessage[] = [];
    public currentMessage = '';
    public isLoading = false;
    public showDisclaimer = false;
    public disclaimerAccepted = false;
    public currentContext!: AppContext;
    public contextDisplay = '';

    private subscriptions: Subscription[] = [];
    private shouldScrollToBottom = false;
    private readonly MAX_MESSAGE_LENGTH = 500;

    constructor(
        public aiChatService: AiChatService,
        public appContextService: AppContextService,
        public userService: UserService,
        private tokenService: HttpTokenService,
        public alertService: AlertService
    ) { }

    ngOnInit(): void {
        // Listen for chat toggle events
        window.addEventListener('toggleChat', this.handleToggleChat.bind(this));

        // Subscribe to messages
        const messagesSub = this.aiChatService.messages$.subscribe(messages => {
            this.messages = messages;
            this.shouldScrollToBottom = true;
        });
        this.subscriptions.push(messagesSub);

        // Subscribe to loading state
        const loadingSub = this.aiChatService.loading$.subscribe(loading => {
            this.isLoading = loading;
            if (loading) {
                this.shouldScrollToBottom = true;
            }
        });
        this.subscriptions.push(loadingSub);

        // Subscribe to context changes
        const contextSub = this.appContextService.context$.subscribe(context => {
            console.log('💬 Chat window received context update:', {
                patient_id: context.patient_id,
                program_id: context.program_id,
                screen_name: context.screen_name,
                active_tab: context.active_tab
            });
            this.currentContext = context;
            this.updateContextDisplay(context);
        });
        this.subscriptions.push(contextSub);

        // Subscribe to logout events (manual logout or session timeout)
        const logoutSub = this.tokenService.logoutObserver.subscribe(() => {
            console.log('🔒 User logged out - closing AI chat window');
            // Close the chat window
            this.isOpen = false;
            this.isMinimized = false;
            // Clear chat history on logout for privacy
            this.aiChatService.clearHistory();
        });
        this.subscriptions.push(logoutSub);

        // Check if disclaimer was already accepted
        this.disclaimerAccepted = localStorage.getItem('aiChatDisclaimerAccepted') === 'true';
    }

    ngAfterViewChecked(): void {
        if (this.shouldScrollToBottom) {
            this.scrollToBottom();
            this.shouldScrollToBottom = false;
        }
    }

    ngOnDestroy(): void {
        window.removeEventListener('toggleChat', this.handleToggleChat.bind(this));
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }

    /**
     * Handle toggle chat event from button
     */
    private handleToggleChat(event: any): void {
        const shouldOpen = event.detail;

        /*   if (shouldOpen && !this.disclaimerAccepted) {
               this.showDisclaimer = true;
           }*/

        this.isOpen = shouldOpen;
        this.isMinimized = false;

        if (this.isOpen) {
            // Scroll to bottom after a short delay to ensure DOM is rendered
            setTimeout(() => {
                this.shouldScrollToBottom = true;
            }, 50);

            // Focus on input after opening
            setTimeout(() => {
                if (this.messageInput) {
                    this.messageInput.nativeElement.focus();
                }
            }, 150);
        }
    }

    /**
     * Update context display for header
     */
    private updateContextDisplay(context: AppContext): void {
        if (context.patient_id && context.program_id) {
            this.contextDisplay = `Patient #${context.patient_id} - Program #${context.program_id}`;
        } else if (context.patient_id) {
            this.contextDisplay = `Patient #${context.patient_id}`;
        } else if (context.screen_name) {
            this.contextDisplay = this.formatScreenName(context.screen_name);
        } else {
            this.contextDisplay = 'General Assistance';
        }
    }

    /**
     * Format screen name for display
     */
    private formatScreenName(screenName: string): string {
        return screenName
            .split('-')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    }

    /**
     * Accept healthcare disclaimer
     */
    public acceptDisclaimer(): void {
        localStorage.setItem('aiChatDisclaimerAccepted', 'true');
        this.disclaimerAccepted = true;
        this.showDisclaimer = false;
    }

    /**
     * Close disclaimer modal (cancel)
     */
    public closeDisclaimer(): void {
        this.showDisclaimer = false;
        this.isOpen = false;
        // Trigger close event
        const event = new CustomEvent('toggleChat', { detail: false });
        window.dispatchEvent(event);
    }

    /**
     * Send message to AI
     */
    public sendMessage(): void {
        if (!this.currentMessage.trim() || this.isLoading) {
            return;
        }

        this.aiChatService.sendMessage(this.currentMessage);
        this.currentMessage = '';
    }

    /**
     * Handle enter key press in input
     */
    public onKeyPress(event: KeyboardEvent): void {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            this.sendMessage();
        }
    }

    /**
     * Use a suggested prompt
     */
    public useSuggestedPrompt(promptText: string): void {
        this.currentMessage = promptText;
        if (this.messageInput) {
            this.messageInput.nativeElement.focus();
        }
    }

    /**
     * Minimize chat window
     */
    public minimize(): void {
        this.isMinimized = !this.isMinimized;
    }

    /**
     * Close chat window
     */
    public close(): void {
        this.isOpen = false;
        this.isMinimized = false;
        // Trigger close event
        const event = new CustomEvent('toggleChat', { detail: false });
        window.dispatchEvent(event);
    }

    /**
     * Toggle left panel visibility
     */
    public toggleLeftPanel(): void {
        this.isLeftPanelOpen = !this.isLeftPanelOpen;
    }

    /**
     * Clear chat history
     */
    public clearHistory(): void {
        this.aiChatService.clearHistory();
        /*  if (confirm('Are you sure you want to clear the chat history?')) {
            this.aiChatService.clearHistory();
          }*/
    }

    /**
     * Copy message content to clipboard
     * @param message - The message to copy
     */
    public copyMessage(message: ChatMessage): void {
        const textToCopy = message.content;

        // Use the Clipboard API if available
        if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(textToCopy).then(() => {
                console.log('✅ Message copied to clipboard');
                // Optional: Show a temporary success message or toast
                this.alertService.success('Message copied to clipboard!', this.alertService.options);
            }).catch(err => {
                console.error('❌ Failed to copy message:', err);
                // Fallback to older method
                this.fallbackCopyToClipboard(textToCopy);
            });
        } else {
            // Fallback for older browsers
            this.fallbackCopyToClipboard(textToCopy);
        }
    }

    /**
     * Fallback copy method for older browsers
     * @param text - Text to copy
     */
    private fallbackCopyToClipboard(text: string): void {
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.left = '-9999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();

        try {
            const successful = document.execCommand('copy');
            if (successful) {
                console.log('✅ Message copied to clipboard (fallback)');
                this.alertService.success('Message copied to clipboard!', this.alertService.options);
            } else {
                console.error('❌ Failed to copy message (fallback)');
                this.alertService.error('Failed to copy message. Please try selecting and copying manually.', this.alertService.options);
            }
        } catch (err) {
            console.error('❌ Fallback copy error:', err);
            this.alertService.error('Failed to copy message. Please try selecting and copying manually.', this.alertService.options);
        }

        document.body.removeChild(textArea);
    }

    /**
     * Scroll messages to bottom
     */
    private scrollToBottom(): void {
        try {
            if (this.messagesContainer) {
                this.messagesContainer.nativeElement.scrollTop =
                    this.messagesContainer.nativeElement.scrollHeight;
            }
        } catch (err) {
            console.error('Error scrolling to bottom:', err);
        }
    }

    /**
     * Get character count status
     */
    public get characterCount(): number {
        return this.currentMessage.length;
    }

    /**
     * Check if message exceeds max length
     */
    public get isMessageTooLong(): boolean {
        return this.currentMessage.length > this.MAX_MESSAGE_LENGTH;
    }

    /**
     * Format timestamp for display
     */
    public formatTimestamp(date: Date): string {
        const now = new Date();
        const messageDate = new Date(date);
        const diffInMinutes = Math.floor((now.getTime() - messageDate.getTime()) / (1000 * 60));

        if (diffInMinutes < 1) {
            return 'Just now';
        } else if (diffInMinutes < 60) {
            return `${diffInMinutes}m ago`;
        } else if (diffInMinutes < 1440) {
            const hours = Math.floor(diffInMinutes / 60);
            return `${hours}h ago`;
        } else {
            return messageDate.toLocaleDateString();
        }
    }

    /**
     * Parse markdown-like formatting in AI responses
     * Simple implementation for bold, italic, lists, and code blocks
     */
    public parseMarkdown(text: string): string {
        if (!text) return '';

        let parsed = text;

        // Headers
        parsed = parsed.replace(/^### (.*$)/gim, '<h3>$1</h3>');
        parsed = parsed.replace(/^## (.*$)/gim, '<h2>$1</h2>');
        parsed = parsed.replace(/^# (.*$)/gim, '<h1>$1</h1>');

        // Bold
        parsed = parsed.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');

        // Italic
        parsed = parsed.replace(/\*(.*?)\*/g, '<em>$1</em>');

        // Code blocks
        parsed = parsed.replace(/```(.*?)```/gs, '<pre><code>$1</code></pre>');

        // Inline code
        parsed = parsed.replace(/`(.*?)`/g, '<code>$1</code>');

        // Unordered lists
        parsed = parsed.replace(/^\- (.*$)/gim, '<li>$1</li>');
        parsed = parsed.replace(/(<li>.*<\/li>)/s, '<ul>$1</ul>');

        // Line breaks
        parsed = parsed.replace(/\n/g, '<br>');

        return parsed;
    }
}
